import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { CreditsResource, CreditTransaction, CustomerCredits, RedeemProductResponse } from '../../core/resources/credits';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface UseCreditsOptions {
    /**
     * Customer ID to fetch credits for
     */
    customerId?: string | null;

    /**
     * Whether to fetch credits automatically on mount
     * @default true
     */
    enabled?: boolean;
}

export interface UseCreditsResult {
    /**
     * Customer credit balance
     */
    balance: number;

    /**
     * Lifetime credits earned
     */
    lifetimeEarned: number;

    /**
     * Lifetime credits spent
     */
    lifetimeSpent: number;

    /**
     * Credit transaction history
     */
    transactions: CreditTransaction[];

    /**
     * Loading state
     */
    isLoading: boolean;

    /**
     * Error state
     */
    error: Error | null;

    /**
     * Refetch credit balance and history
     */
    refetch: () => Promise<void>;

    /**
     * Redeem a product using credits
     */
    redeemProduct: (data: {
        productId?: string;
        variantId: string;
        quantity?: number;
    }) => Promise<RedeemProductResponse>;

    /**
     * Check if customer has enough credits for a purchase
     */
    hasEnoughCredits: (amount: number) => boolean;

    /**
     * Get transaction by ID
     */
    getTransaction: (transactionId: string) => CreditTransaction | undefined;

    /**
     * Redemption mutation state
     */
    isRedeeming: boolean;

    /**
     * Redemption error state
     */
    redeemError: Error | null;
}

export function useCredits(options: UseCreditsOptions = {}): UseCreditsResult {
    const { customerId = null, enabled = true } = options;
    const { storeId } = usePluginConfig();
    const { isSessionInitialized } = useTagadaContext();
    const queryClient = useQueryClient();

    // Create credits resource client
    const creditsResource = useMemo(() => {
        try {
            return new CreditsResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(
                'Failed to initialize credits resource: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
        }
    }, []);

    // Determine if query should be enabled
    const isEnabled = useMemo(() => {
        return Boolean(enabled && customerId && storeId && isSessionInitialized);
    }, [enabled, customerId, storeId, isSessionInitialized]);

    // Create query key
    const queryKey = useMemo(
        () => ['customer-credits', { customerId, storeId }],
        [customerId, storeId]
    );

    // Fetch customer credits using TanStack Query
    const {
        data = null,
        isLoading,
        error,
        refetch,
    } = useQuery<CustomerCredits | null>({
        queryKey,
        enabled: isEnabled,
        queryFn: async () => {
            if (!customerId || !storeId) {
                return null;
            }

            return await creditsResource.getCustomerCredits(customerId, storeId);
        },
        staleTime: 30 * 1000, // 30 seconds - fresher than most resources
        refetchOnWindowFocus: true, // Refetch on window focus for up-to-date balance
    });

    // Redeem product mutation
    const redeemMutation = useMutation({
        mutationFn: async (redeemData: {
            productId?: string;
            variantId: string;
            quantity?: number;
        }) => {
            return await creditsResource.redeemProduct(redeemData);
        },
        onSuccess: () => {
            // Invalidate credits query to refetch updated balance
            void queryClient.invalidateQueries({ queryKey });
        },
        onError: (error: Error) => {
            console.error('[SDK] Failed to redeem product with credits:', error);
        },
    });

    // Helper functions
    const hasEnoughCredits = useCallback(
        (amount: number): boolean => {
            return (data?.balance ?? 0) >= amount;
        },
        [data?.balance]
    );

    const getTransaction = useCallback(
        (transactionId: string): CreditTransaction | undefined => {
            return data?.transactions.find((tx) => tx.id === transactionId);
        },
        [data?.transactions]
    );

    const redeemProduct = useCallback(
        async (redeemData: {
            productId?: string;
            variantId: string;
            quantity?: number;
        }): Promise<RedeemProductResponse> => {
            return await redeemMutation.mutateAsync(redeemData);
        },
        [redeemMutation]
    );

    return {
        balance: data?.balance ?? 0,
        lifetimeEarned: data?.lifetimeEarned ?? 0,
        lifetimeSpent: data?.lifetimeSpent ?? 0,
        transactions: data?.transactions ?? [],
        isLoading,
        error,
        refetch: async () => {
            await refetch();
        },
        redeemProduct,
        hasEnoughCredits,
        getTransaction,
        isRedeeming: redeemMutation.isPending,
        redeemError: redeemMutation.error,
    };
}

